import { entityPaths } from '@lakaran/geometry';
import { mirrorAxis, pickablePieces, type NodeGraph } from '@lakaran/nodes';
import type { CadEditor } from '../src/editor/editor';
import {
  createPieceHighlighter,
  createPiecePicker,
  type PickPiece,
  type PieceHighlighter,
  type PiecePicker,
} from './piece-picker';

/**
 * Mirror picked in the drawing.
 *
 * A mirror step reflects either the whole incoming stream or only the pieces chosen here: clicking
 * a piece toggles it into the selection, and the axis it reflects across is drawn while the mode is
 * armed so the reflection is never a guess. `X` / `Esc` / right-click ends the mode.
 */

export interface MirrorPickerOptions {
  graph: () => NodeGraph;
  /** Adds or removes one piece from the selection. */
  onToggle: (nodeId: string, key: string, picked: boolean) => void;
  onEnd?: () => void;
}

export type MirrorPicker = PiecePicker;
export type MirrorHighlighter = PieceHighlighter;

export function createMirrorPicker(
  editor: CadEditor,
  host: HTMLElement,
  options: MirrorPickerOptions,
): MirrorPicker {
  const pieces = (nodeId: string): PickPiece[] =>
    pickablePieces(options.graph(), nodeId, editor.doc.currentLayerId).map((piece) => ({
      key: piece.key,
      label: piece.label,
      on: piece.picked,
      paths: entityPaths(piece.entity),
    }));

  return createPiecePicker(editor, host, {
    pieces,
    onToggle: (nodeId, key, picked) => options.onToggle(nodeId, key, picked),
    hint: 'Mirror — click a piece to reflect it, click again to drop it, X or Esc ends',
    startMessage: (count) =>
      `MIRROR  ${count} pieces in this step — click the ones to reflect (X ends).`,
    emptyMessage: 'MIRROR  Nothing to reflect — link a stream into the mirror step first.',
    action: (piece) => (piece.on ? 'click to drop it' : 'click to reflect it'),
    onClass: 'is-picked',
    decorate: (nodeId, svg) => {
      const axis = mirrorAxis(options.graph(), nodeId, editor.doc.currentLayerId);
      if (!axis) return;
      const a = editor.viewport.worldToScreen(axis.a);
      const b = editor.viewport.worldToScreen(axis.b);
      const direction = b.sub(a);
      const length = Math.hypot(direction.x, direction.y);
      if (length < 1e-6) return;
      // Stretch the axis past both edges of the viewport so it reads as a line, not a segment.
      const reach = Math.hypot(editor.viewport.width, editor.viewport.height);
      const unit = direction.scale(reach / length);
      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line.setAttribute('x1', String(a.x - unit.x));
      line.setAttribute('y1', String(a.y - unit.y));
      line.setAttribute('x2', String(a.x + unit.x));
      line.setAttribute('y2', String(a.y + unit.y));
      line.setAttribute('class', 'mirror-axis');
      svg.append(line);
    },
    onEnd: options.onEnd,
  });
}

/** Hovering a picked row in the card lights up the piece it would drop. */
export function createMirrorHighlighter(
  editor: CadEditor,
  host: HTMLElement,
  options: { graph: () => NodeGraph },
): MirrorHighlighter {
  return createPieceHighlighter(editor, host, {
    pieces: (nodeId) =>
      pickablePieces(options.graph(), nodeId, editor.doc.currentLayerId).map((piece) => ({
        key: piece.key,
        label: piece.label,
        on: piece.picked,
        paths: entityPaths(piece.entity),
      })),
    hotClass: 'is-picked',
  });
}
